import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Button,
  CircularProgress,
  Pagination,
} from "@mui/material";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import { useNavigate } from "react-router-dom";
import logoMatches from "../../static/Home/logo_matches.webp";

const ITEMS_PER_PAGE = 6;

function TeamLogo({ src, alt }) {
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: { xs: 56, sm: 74 },
          height: { xs: 56, sm: 74 },
          objectFit: "contain",
          filter: "drop-shadow(0 8px 14px rgba(6,78,42,0.14))",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: { xs: 56, sm: 74 },
        height: { xs: 56, sm: 74 },
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #ffffff 0%, #e8f5ec 100%)",
        border: "1px solid rgba(6,78,42,0.20)",
        boxShadow: "0 8px 18px rgba(6,78,42,0.14)",
      }}
    >
      <SportsSoccerRoundedIcon
        sx={{
          fontSize: { xs: 32, sm: 44 },
          color: "#064e2a",
        }}
      />
    </Box>
  );
}

export default function SchedulePage() {
  const navigate = useNavigate();

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().slice(0, 10);

  const [selectedDate, setSelectedDate] = useState(today);
  const [page, setPage] = useState(1);

  const DARK_GREEN = "#052e16";
  const GREEN = "#064e2a";
  const MID_GREEN = "#15803d";
  const SOFT_GREEN = "#f4fbf6";
  const LIGHT_GREEN = "#e8f5ec";

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      const res = await fetch("/matches/public/");
      const data = await res.json();

      if (Array.isArray(data)) {
        setMatches(data);
      } else {
        setMatches([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const availableDates = useMemo(() => {
    const unique = [...new Set(matches.map((m) => m.date))];
    return unique.sort();
  }, [matches]);

  useEffect(() => {
    if (!availableDates.includes(selectedDate) && availableDates.length) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  const filteredMatches = useMemo(() => {
    return matches
      .filter((match) => match.date === selectedDate)
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [matches, selectedDate]);

  const paginatedMatches = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredMatches.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMatches, page]);

  const totalPages = Math.ceil(filteredMatches.length / ITEMS_PER_PAGE);

  const formatDateButton = (date) => {
    const d = new Date(date + "T12:00:00");

    const weekday = d.toLocaleDateString("es-EC", {
      weekday: "short",
    });

    const day = d.getDate();

    return `${weekday.toUpperCase()} ${day}`;
  };

  const getScore = (match, side) => {
    if (match.status === "scheduled") return "-";

    return side === "local"
      ? match.score_local ?? 0
      : match.score_visitor ?? 0;
  };

  const getStatusLabel = (status) => {
    if (status === "live") return "EN VIVO";
    if (status === "finished") return "FINALIZADO";
    if (status === "scheduled") return "PROGRAMADO";
    return "CANCELADO";
  };

  const getStatusColor = (status) => {
    if (status === "live") return "#d32f2f";
    if (status === "finished") return MID_GREEN;
    if (status === "scheduled") return GREEN;
    return "#616161";
  };

  const getStatusBg = (status) => {
    if (status === "live") return "rgba(211,47,47,0.12)";
    if (status === "finished") return "rgba(21,128,61,0.14)";
    if (status === "scheduled") return "rgba(6,78,42,0.12)";
    return "rgba(97,97,97,0.14)";
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #ffffff 0%, #f4fbf6 48%, #e8f5ec 100%)",
        py: { xs: 5, md: 7 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(6,78,42,0.12), transparent 70%)",
          top: -150,
          left: -120,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 440,
          height: 440,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(21,128,61,0.12), transparent 70%)",
          bottom: -190,
          right: -140,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.06,
          backgroundImage:
            "linear-gradient(#064e2a 1px, transparent 1px), linear-gradient(90deg, #064e2a 1px, transparent 1px)",
          backgroundSize: "54px 54px",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Typography
          sx={{
            textAlign: "center",
            mb: 0.8,
            fontWeight: 900,
            fontSize: { xs: "0.78rem", md: "0.9rem" },
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: GREEN,
          }}
        >
          Liga Deportiva Cantonal Mejía
        </Typography>

        <Typography
          component="h1"
          sx={{
            textAlign: "center",
            fontWeight: 950,
            fontSize: { xs: "2rem", md: "3rem" },
            color: DARK_GREEN,
            lineHeight: 1.1,
            mb: 1,
          }}
        >
          Calendario de partidos
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            color: "#3f5f4b",
            fontWeight: 600,
            fontSize: { xs: ".95rem", md: "1.05rem" },
            mb: { xs: 3, md: 4 },
          }}
        >
          Consulta la programación oficial, horarios, sedes y marcadores del torneo de fútbol
        </Typography>

        {loading ? (
          <Stack alignItems="center" sx={{ mt: 6 }}>
            <CircularProgress sx={{ color: GREEN }} />
          </Stack>
        ) : (
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mt: 2,
                mb: 5,
                overflowX: "auto",
                pb: 1,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(6,78,42,0.22)",
                  borderRadius: "999px",
                },
              }}
            >
              {availableDates.map((date) => {
                const active = selectedDate === date;

                return (
                  <Button
                    key={date}
                    variant={active ? "contained" : "outlined"}
                    onClick={() => {
                      setSelectedDate(date);
                      setPage(1);
                    }}
                    sx={{
                      minWidth: 98,
                      height: 44,
                      borderRadius: "14px",
                      fontWeight: 900,
                      textTransform: "none",
                      borderColor: active ? GREEN : "rgba(6,78,42,0.22)",
                      color: active ? "#fff" : GREEN,
                      backgroundColor: active ? GREEN : "#fff",
                      boxShadow: active
                        ? "0 10px 24px rgba(6,78,42,0.22)"
                        : "none",
                      "&:hover": {
                        backgroundColor: active ? DARK_GREEN : SOFT_GREEN,
                        borderColor: DARK_GREEN,
                        color: active ? "#fff" : DARK_GREEN,
                      },
                    }}
                  >
                    {formatDateButton(date)}
                  </Button>
                );
              })}
            </Stack>

            {filteredMatches.length === 0 ? (
              <Typography
                align="center"
                sx={{
                  color: GREEN,
                  fontWeight: 800,
                  py: 5,
                }}
              >
                No hay partidos programados
              </Typography>
            ) : (
              <>
                <Stack spacing={3}>
                  {paginatedMatches.map((match) => (
                    <Card
                      key={match.id}
                      elevation={0}
                      sx={{
                        borderRadius: "26px",
                        p: { xs: 2, md: 3 },
                        background:
                          "linear-gradient(180deg, #ffffff 0%, #fbfffc 100%)",
                        border: "1px solid rgba(6,78,42,0.12)",
                        boxShadow: "0 22px 52px rgba(6,78,42,0.13)",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={{ xs: 2.2, md: 2 }}
                      >
                        <Stack
                          alignItems="center"
                          spacing={1}
                          sx={{
                            width: { xs: "100%", md: 170 },
                            minWidth: 0,
                          }}
                        >
                          <TeamLogo
                            src={match.team_local_logo}
                            alt={match.team_local}
                          />

                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: DARK_GREEN,
                              textAlign: "center",
                              lineHeight: 1.2,
                            }}
                          >
                            {match.team_local}
                          </Typography>
                        </Stack>

                        <Stack
                          alignItems="center"
                          spacing={1.2}
                          sx={{
                            width: { xs: "100%", md: "auto" },
                          }}
                        >
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.65,
                              borderRadius: "999px",
                              backgroundColor: getStatusBg(match.status),
                              color: getStatusColor(match.status),
                              fontWeight: 950,
                              fontSize: ".78rem",
                              lineHeight: 1,
                            }}
                          >
                            {getStatusLabel(match.status)}
                          </Box>

                          <Stack
                            direction="row"
                            spacing={{ xs: 0.7, sm: 1.2 }}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                minWidth: { xs: 58, sm: 72 },
                                height: { xs: 62, sm: 74 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "18px",
                                background:
                                  "linear-gradient(180deg,#ffffff 0%,#f0fdf4 100%)",
                                border: "1px solid rgba(6,78,42,0.18)",
                                boxShadow: "0 12px 26px rgba(6,78,42,0.10)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  fontSize: { xs: "2rem", sm: "2.7rem" },
                                  color: GREEN,
                                  lineHeight: 1,
                                }}
                              >
                                {getScore(match, "local")}
                              </Typography>
                            </Box>

                            <Box
                              component="img"
                              src={logoMatches}
                              alt="vs"
                              sx={{
                                width: { xs: 28, sm: 34 },
                                height: { xs: 28, sm: 34 },
                                objectFit: "contain",
                              }}
                            />

                            <Box
                              sx={{
                                minWidth: { xs: 58, sm: 72 },
                                height: { xs: 62, sm: 74 },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "18px",
                                background:
                                  "linear-gradient(180deg,#ffffff 0%,#f0fdf4 100%)",
                                border: "1px solid rgba(6,78,42,0.18)",
                                boxShadow: "0 12px 26px rgba(6,78,42,0.10)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 950,
                                  fontSize: { xs: "2rem", sm: "2.7rem" },
                                  color: GREEN,
                                  lineHeight: 1,
                                }}
                              >
                                {getScore(match, "visitor")}
                              </Typography>
                            </Box>
                          </Stack>

                          <Typography
                            sx={{
                              color: "#3f5f4b",
                              fontWeight: 800,
                              textAlign: "center",
                            }}
                          >
                            {match.time} · {match.venue}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#3f5f4b",
                              fontWeight: 800,
                              letterSpacing: ".04em",
                              textAlign: "center",
                            }}
                          >
                            {match.category}
                          </Typography>
                        </Stack>

                        <Stack
                          alignItems="center"
                          spacing={1}
                          sx={{
                            width: { xs: "100%", md: 170 },
                            minWidth: 0,
                          }}
                        >
                          <TeamLogo
                            src={match.team_visitor_logo}
                            alt={match.team_visitor}
                          />

                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: DARK_GREEN,
                              textAlign: "center",
                              lineHeight: 1.2,
                            }}
                          >
                            {match.team_visitor}
                          </Typography>
                        </Stack>
                      </Stack>

                      {match.status === "live" && (
                        <Stack alignItems="center" sx={{ mt: 3 }}>
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/partido/${match.id}`)}
                            sx={{
                              minWidth: 180,
                              height: 46,
                              backgroundColor: GREEN,
                              color: "#fff",
                              fontWeight: 900,
                              textTransform: "none",
                              borderRadius: "14px",
                              px: 3,
                              boxShadow: "0 12px 26px rgba(6,78,42,0.24)",
                              "&:hover": {
                                backgroundColor: DARK_GREEN,
                                transform: "scale(1.05)",
                                boxShadow: "0 16px 34px rgba(6,78,42,0.32)",
                              },
                            }}
                          >
                            Ver partido en vivo
                          </Button>
                        </Stack>
                      )}
                    </Card>
                  ))}
                </Stack>

                {totalPages > 1 && (
                  <Stack alignItems="center" sx={{ mt: 5 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontWeight: 800,
                          color: GREEN,
                          borderColor: "rgba(6,78,42,0.25)",
                        },
                        "& .Mui-selected": {
                          backgroundColor: `${GREEN} !important`,
                          color: "#fff !important",
                        },
                      }}
                    />
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}